import * as React from 'react';
import {
    Box,
    Chip,
    Divider,
    Grid,
    Paper,
    Typography,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import EventIcon from '@mui/icons-material/Event';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import {
    NormalizedAssetData,
    MacroSensitivity,
} from '../../api/asset-tab-types';

interface OverviewTabProps {
    data: NormalizedAssetData;
}

const STRENGTH_COLOR: Record<string, 'error' | 'warning' | 'info'> = {
    high: 'error',
    medium: 'warning',
    low: 'info',
};

const RELATIONSHIP_ICON: Record<string, React.ReactNode> = {
    positive: (
        <ArrowUpwardIcon fontSize="small" sx={{ color: 'success.main' }} />
    ),
    negative: (
        <ArrowDownwardIcon fontSize="small" sx={{ color: 'error.main' }} />
    ),
    'non-linear': (
        <SyncAltIcon fontSize="small" sx={{ color: 'warning.main' }} />
    ),
};

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, mt: 0.5 }}>
        {children}
    </Typography>
);

const SectionPaper = ({ children }: { children: React.ReactNode }) => (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
        {children}
    </Paper>
);

const MacroSensitivityCard = ({ s }: { s: MacroSensitivity }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: '100%',
        }}
    >
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={0.5}>
                {RELATIONSHIP_ICON[s.relationship]}
                <Typography variant="subtitle2" fontWeight={600}>
                    {s.indicator}
                </Typography>
            </Box>
            <Chip
                label={s.strength.toUpperCase()}
                size="small"
                color={STRENGTH_COLOR[s.strength]}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }}
            />
        </Box>
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
        >
            {s.mechanism}
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={0.5} mt="auto">
            {s.alternative_names?.map(n => (
                <Chip
                    key={n}
                    label={n}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                />
            ))}
        </Box>
    </Paper>
);

const stripCiteTags = (text: string): string =>
    text.replace(/<cite[^>]*>(.*?)<\/cite>/gs, '$1');

const OverviewTab = ({ data }: OverviewTabProps) => {
    const tech = data.asset_specific?.technology;

    return (
        <Box
            sx={{
                p: { xs: 2, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}
        >
            {/* Macro Sensitivities */}
            <Box>
                <SectionHeader>Macro Sensitivities</SectionHeader>
                <Grid container spacing={1.5}>
                    {data.market_drivers.macro_sensitivities.map(s => (
                        <Grid item xs={12} sm={6} lg={3} key={s.indicator}>
                            <MacroSensitivityCard s={s} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Sector Dependencies + Geopolitical */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <SectionPaper>
                        <SectionHeader>Sector Dependencies</SectionHeader>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            {data.market_drivers.sector_dependencies?.map(
                                sd => (
                                    <Box key={sd.sector}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            mb={0.5}
                                        >
                                            <Typography variant="subtitle2">
                                                {sd.sector}
                                            </Typography>
                                            <Chip
                                                label={sd.strength.toUpperCase()}
                                                size="small"
                                                color={
                                                    STRENGTH_COLOR[sd.strength]
                                                }
                                                variant="outlined"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    height: 20,
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.5 }}
                                        >
                                            {sd.relationship}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionPaper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <SectionPaper>
                        <SectionHeader>Geopolitical Factors</SectionHeader>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                            {data.market_drivers.geopolitical_factors?.map(
                                gf => (
                                    <Box key={gf.factor}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            mb={0.5}
                                        >
                                            <Typography variant="subtitle2">
                                                {gf.factor}
                                            </Typography>
                                            <Chip
                                                label={gf.strength.toUpperCase()}
                                                size="small"
                                                color={
                                                    STRENGTH_COLOR[gf.strength]
                                                }
                                                variant="outlined"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.65rem',
                                                    height: 20,
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.5 }}
                                        >
                                            {gf.impact}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    </SectionPaper>
                </Grid>
            </Grid>

            {/* Market Structure */}
            {data.market_structure && (
                <SectionPaper>
                    <SectionHeader>Market Structure</SectionHeader>
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                        {data.market_structure.market_position}
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mb={0.5}
                            >
                                Primary Markets
                            </Typography>
                            {data.market_structure.primary_markets?.map(pm => (
                                <Typography
                                    key={pm}
                                    variant="body2"
                                    sx={{
                                        mb: 0.5,
                                        pl: 1,
                                        borderLeft: 2,
                                        borderColor: 'primary.main',
                                    }}
                                >
                                    {pm}
                                </Typography>
                            ))}
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        mb={0.5}
                                    >
                                        Competitors
                                    </Typography>
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={0.5}
                                    >
                                        {data.market_structure.related_assets?.competitors?.map(
                                            c => (
                                                <Chip
                                                    key={c}
                                                    label={c}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        mb={0.5}
                                    >
                                        Complements
                                    </Typography>
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={0.5}
                                    >
                                        {data.market_structure.related_assets?.complements?.map(
                                            c => (
                                                <Chip
                                                    key={c}
                                                    label={c}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        mb={0.5}
                                    >
                                        Derivatives
                                    </Typography>
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={0.5}
                                    >
                                        {data.market_structure.related_assets?.derivatives?.map(
                                            d => (
                                                <Chip
                                                    key={d}
                                                    label={d}
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            )
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.5}
                    >
                        Regulatory Status
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                    >
                        {data.market_structure.regulatory_status
                            ? stripCiteTags(
                                  data.market_structure.regulatory_status
                              )
                            : null}
                    </Typography>
                </SectionPaper>
            )}

            {/* Events & Catalysts */}
            {data.events_and_catalysts && (
                <SectionPaper>
                    <SectionHeader>Events & Catalysts</SectionHeader>
                    <Grid container spacing={2}>
                        {/* Recurring Events */}
                        <Grid item xs={12} md={4}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                mb={1}
                            >
                                <ScheduleIcon fontSize="small" color="action" />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                >
                                    Recurring Events
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={1.5}
                            >
                                {data.events_and_catalysts.recurring_events?.map(
                                    re => (
                                        <Box key={re.type}>
                                            <Typography variant="subtitle2">
                                                {re.type}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                            >
                                                {re.frequency}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.25,
                                                    lineHeight: 1.4,
                                                    fontSize: '0.8rem',
                                                }}
                                            >
                                                {re.impact}
                                            </Typography>
                                            {re.next_date && (
                                                <Chip
                                                    icon={
                                                        <EventIcon
                                                            sx={{
                                                                fontSize: 14,
                                                            }}
                                                        />
                                                    }
                                                    label={`Next: ${stripCiteTags(re.next_date)}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{
                                                        mt: 0.5,
                                                        fontSize: '0.7rem',
                                                        height: 22,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    )
                                )}
                            </Box>
                        </Grid>

                        {/* Scheduled Events */}
                        <Grid item xs={12} md={4}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                mb={1}
                            >
                                <EventIcon fontSize="small" color="action" />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                >
                                    Scheduled Events
                                </Typography>
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1}>
                                {data.events_and_catalysts.scheduled_events?.map(
                                    (se, i) => (
                                        <Typography
                                            key={i}
                                            variant="body2"
                                            sx={{
                                                pl: 1.5,
                                                borderLeft: 2,
                                                borderColor: 'warning.main',
                                                lineHeight: 1.5,
                                                fontSize: '0.8rem',
                                            }}
                                        >
                                            {stripCiteTags(se)}
                                        </Typography>
                                    )
                                )}
                            </Box>
                        </Grid>

                        {/* Historical Events */}
                        <Grid item xs={12} md={4}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                mb={1}
                            >
                                <HistoryIcon fontSize="small" color="action" />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                >
                                    Historical Events
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                            >
                                {data.events_and_catalysts.historical_events?.map(
                                    (he, i) => (
                                        <Typography
                                            key={i}
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.8rem',
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {he}
                                        </Typography>
                                    )
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </SectionPaper>
            )}

            {/* Asset-Specific: Crypto Technology */}
            {tech && (
                <SectionPaper>
                    <SectionHeader>Technology Details</SectionHeader>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={1.5}
                            >
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Platform Type
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {tech.platform_type}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Consensus Mechanism
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {tech.consensus_mechanism}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Hashing Algorithm
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {tech.hashing_algorithm}
                                    </Typography>
                                </Box>
                                {(tech.block_time ?? tech.block_time_minutes) !=
                                    null && (
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Block Time
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {tech.block_time ??
                                                `${tech.block_time_minutes} minutes`}
                                        </Typography>
                                    </Box>
                                )}
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Current Mining Reward
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                    >
                                        {tech.mining_reward_current} BTC per
                                        block
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={1.5}
                            >
                                {tech.max_supply != null && (
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Max Supply
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {tech.max_supply.toLocaleString()}
                                        </Typography>
                                    </Box>
                                )}
                                {tech.current_circulating_supply_approx !=
                                    null && (
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Circulating Supply
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {tech.current_circulating_supply_approx.toLocaleString()}
                                        </Typography>
                                    </Box>
                                )}
                                {tech.supply_mined_percentage != null && (
                                    <Box>
                                        <Tooltip
                                            title={`${tech.supply_mined_percentage}% of max supply`}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Supply Mined
                                                </Typography>
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1}
                                                >
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={
                                                            tech.supply_mined_percentage
                                                        }
                                                        sx={{
                                                            flex: 1,
                                                            height: 8,
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                    >
                                                        {
                                                            tech.supply_mined_percentage
                                                        }
                                                        %
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                )}
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        mb={0.5}
                                    >
                                        Key Features
                                    </Typography>
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={0.5}
                                    >
                                        {tech.key_features?.map(f => (
                                            <Chip
                                                key={f}
                                                label={f}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </SectionPaper>
            )}

            {/* Description */}
            <SectionPaper>
                <SectionHeader>Description</SectionHeader>
                <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                    {data.description.short_summary}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mb={1.5}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ width: '100%' }}
                    >
                        Primary Use Cases
                    </Typography>
                    {data.description.primary_use_cases?.map(uc => (
                        <Chip
                            key={uc}
                            label={uc}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    ))}
                </Box>
                <Box display="flex" flexWrap="wrap" gap={1}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ width: '100%' }}
                    >
                        Key Characteristics
                    </Typography>
                    {data.description.key_characteristics?.map(kc => (
                        <Chip key={kc} label={kc} size="small" />
                    ))}
                </Box>
            </SectionPaper>
        </Box>
    );
};

export default OverviewTab;
